// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { UNSAFE_media, media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import Image from "@/components/wpl/image/src";
import { BGProps, BGStyles } from "@/libraries/wpl/foundations/src";
import CTAPrimary from "@/patterns/wpl/cta-primary/src";

const backgroundProperties: BGProps = {
    partialBackgrounds: [
        {
            imageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:d281b49a-9812-46dd-996c-e35789461eec/CareersRight_540x450.svg?cdnVersion=1221",
            positionDesktop: "cornerBottomRight",
            positionTablet: "cornerBottomRight",
            positionMobile: "hidden"
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:76ed2bec-4e88-4341-b0ce-83ede8e68015/CareersLeft_540x450.svg?cdnVersion=1221",
            positionDesktop: "cornerBottomLeft",
            positionTablet: "cornerBottomLeft",
            positionMobile: "hidden"
        }
    ]
};

// style overrides to match existing WAC home-page
const careersStyle = css`
    ${UNSAFE_media.only.sm} {
        background-position: bottom left -200px, bottom right -200px;
        background-size: contain, contain;
    }
    ${media.above.sm} {
        padding-bottom: 96px;
    }
    ${media.above.md} {
        background-size: contain, contain;
    }
    ${UNSAFE_media.only.md} {
        background-position: bottom left -150px, bottom right -150px;
    }
    padding-top: 64px;

    .row-container {
        max-width: 1440px;
        margin-left: auto;
        margin-right: auto;
    }

    .cta-primary {
        ${media.above.sm} {
            padding-bottom: 64px;
        }
        padding-bottom: 0;
        padding-top: 8px;
    }

    .row-layout {
        ${UNSAFE_media.only.md} {
            margin-left: 25%;
            width: 50%;
        }
        ${UNSAFE_media.only.sm} {
            margin-left: 16.66666%;
            width: 66.66666%;
        }
        ${media.above.lg} {
            margin-left: 33.33333%;
            width: 33.33333%;
        }

        padding-left: 16px;
        padding-right: 16px;
        width: 100%;
    }

    h3.contentBlock-eyebrow {
        ${media.above.md} {
            margin-bottom: 16px;
        }
        color: #2684ff;
        font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI,
            Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
        font-size: 16px;
        font-weight: 800;
        letter-spacing: 1.6px;
        line-height: 2;
        margin-bottom: 10px;
        -webkit-font-smoothing: antialiased;
    }

    .primary-cta-content {
        h2 {
            color: #253858;
            font-size: 2rem;
            letter-spacing: 0;
            margin-top: 0;
            line-height: 1.25;
            margin-bottom: 14px;

            ${media.above.sm} {
                font-size: 2.75rem;
                line-height: 1.18181818;
                margin-bottom: 16px;
            }
            ${media.above.lg} {
                font-size: 3rem;
                line-height: 3.5rem;
                margin-bottom: 24px;
            }
        }

        .contentBlock-body {
            font-family: "Charlie Display", -apple-system, "system-ui",
                "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans",
                "Helvetica Neue", sans-serif;
            font-size: 1.25rem;
            letter-spacing: 0.3px;
            color: #42526e;
            margin-bottom: 24px;
        }

        .contentBlock-linkFlex {
            margin-bottom: 0;

            a {
                margin-top: 0;
            }
        }
    }

    .mobile-image-container {
        ${media.above.sm} {
            display: none;
        }
        margin-top: -50px;
        padding: 16px 16px 0 16px;
        position: relative;
        z-index: -1;
    }

    .mobile-image-container img {
        height: auto;
        width: 100%;
        max-width: 100%;
        vertical-align: bottom;
    }
`;
export const Careers = () => {
    const intl = useIntl();

    return (
        <UFOSegment name="careers">
            <section
                className="careers"
                css={[BGStyles(backgroundProperties), careersStyle]}
            >
                <div className="row-container">
                    <CTAPrimary
                        className="cta-primary"
                        eyebrowText={intl.formatMessage({
                            id: "HomePageTemplate.careersEyebrowText",
                            defaultMessage: "Careers"
                        })}
                        headerText={intl.formatMessage({
                            id: "HomePageTemplate.careersCTAHeaderText",
                            defaultMessage: "We can’t do it alone"
                        })}
                        bodyText={intl.formatMessage({
                            id: "HomePageTemplate.careersCTABodyText",
                            defaultMessage:
                                "We have an ambitious road ahead, and we’re looking for people to join our global team to help shape the future of Atlassian."
                        })}
                        ctaProps={{
                            buttonProps: {
                                text: intl.formatMessage({
                                    id: "HomePageTemplate.careersButtonText",
                                    defaultMessage: "Join the team"
                                }),
                                url: useLocalizedHref("/company/careers")
                            }
                        }}
                    />
                    <div className="mobile-image-container">
                        <Image
                            loading="lazy"
                            alt={intl.formatMessage({
                                id: "HomePageTemplate.careersMobileImageAltText",
                                defaultMessage: "Atlassian Careers"
                            })}
                            src="https://wac-cdn.atlassian.com/dam/jcr:ee14953a-98f7-41d9-a2f9-21eebf69f8b9/CareersCenterMobile.svg?cdnVersion=1223"
                        />
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};
